import { useEffect, useState } from "react";
import type {
  BookPickCourierRequest,
  BookPickCourierResponse,
  SupplierPickPage,
} from "@shared-core/data/supplier-picks-adapter";
import { useSupplierPicksAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  COURIER_WORDING,
  PICK_WORDING,
  count,
  pickDestinationLabel,
  stillToGo,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { FormField } from "../../app/components/patterns/FormField";

type BookedCarrier = NonNullable<BookPickCourierRequest["carrier"]>;
interface Option {
  value: string;
  label: string;
}

/**
 * *Book with DPD / DX and print label* for one destination of the pick (courier chain): the couriers booked here
 * come from the dry run, so the panel shows only where the server offers one, closed until the warehouse opens it
 * (recording a despatch by hand stays the plain path). Choosing a courier prefills the parcels
 * from the stock item's weight (the warehouse corrects them as packed) and offers its services, by 12 the default.
 * The booking records the despatch under the courier's number; the request id minted when the form opens makes a
 * retry answer the same booking rather than book twice.
 */
export function BookCourierForm({
  pick,
  onBooked,
}: {
  pick: SupplierPickPage;
  onBooked: (booked: BookPickCourierResponse) => void;
}) {
  const picks = useSupplierPicksAdapter();
  const open = pick.deliver_to.filter((d) => stillToGo(d) > 0);
  const [requestId] = useState(() => crypto.randomUUID());
  const [destinationId, setDestinationId] = useState(
    open[0]?.delivery_line_id ?? "",
  );
  const destination = open.find((d) => d.delivery_line_id === destinationId);
  const [quantity, setQuantity] = useState(
    String(destination ? stillToGo(destination) : ""),
  );
  const [despatchedOn, setDespatchedOn] = useState(pick.today);
  const [carriers, setCarriers] = useState<Option[]>([]);
  const [carrier, setCarrier] = useState<BookedCarrier | "">("");
  const [services, setServices] = useState<Option[]>([]);
  const [serviceCode, setServiceCode] = useState("");
  const [parcels, setParcels] = useState<string[]>([]);
  const [parcelsEdited, setParcelsEdited] = useState(false);
  const [contents, setContents] = useState("");
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const write = useFieldErrorWrite("We couldn't book this with the courier.");
  const errors = write.fieldErrors;

  const quantitySent = quantity.trim() === "" ? null : Number(quantity);
  const request = (): BookPickCourierRequest => ({
    despatched_on: despatchedOn,
    carrier: carrier || null,
    service_code: serviceCode || null,
    parcels: parcels.map((weight) => ({ weight_kg: Number(weight) })),
    contents: contents.trim() || null,
    delivery_line_id: destinationId || null,
    quantity: quantitySent,
    request_id: requestId,
  });

  // The dry run: re-asked when the courier, the destination or the quantity changes.
  const previewKey = JSON.stringify([carrier, destinationId, quantitySent]);
  useEffect(() => {
    let stale = false;
    picks
      .previewPickCourierBooking(pick.pick_id, {
        ...request(),
        service_code: null,
        parcels: parcelsEdited ? request().parcels : [],
      })
      .then((dry) => {
        if (stale) return;
        const options = (key: string) =>
          dry.choices
            .find((c) => c.key === key)
            ?.options.map((o) => ({ value: o.value, label: o.label })) ?? [];
        setCarriers(options("carrier"));
        setServices(options("service_code"));
        const prefill = (dry.prefill ?? {}) as {
          parcels?: { weight_kg: number }[];
          service_code?: string;
          contents?: string;
        };
        if (prefill.parcels && !parcelsEdited)
          setParcels(prefill.parcels.map((p) => String(p.weight_kg)));
        if (prefill.service_code) setServiceCode(prefill.service_code);
        if (prefill.contents) setContents((c) => c || prefill.contents!);
        const problem = dry.checks.find((c) => c.state !== "pass");
        setPreviewNote(problem ? problem.label : null);
      })
      .catch(() => {
        if (!stale) setPreviewNote(COURIER_WORDING.couldNotAsk);
      });
    return () => {
      stale = true;
    };
    // The request's other fields only matter alongside a change in what previewKey serialises.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, pick.pick_id, previewKey]);

  if (carriers.length === 0) return null;
  if (!isOpen) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4">
        <div>
          <p className="text-sm font-semibold">{COURIER_WORDING.title}</p>
          <p className="text-sm text-muted-foreground">
            {COURIER_WORDING.hint}
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setIsOpen(true)}>
          {COURIER_WORDING.open}
        </Button>
      </div>
    );
  }
  const carrierName = carriers.find((c) => c.value === carrier)?.label;

  const setParcel = (index: number, weight: string) => {
    setParcelsEdited(true);
    setParcels((current) => current.map((w, i) => (i === index ? weight : w)));
  };

  const submit = () =>
    void write.run(async () => {
      onBooked(await picks.bookPickCourier(pick.pick_id, request()));
    });

  return (
    <form
      noValidate
      aria-label={COURIER_WORDING.title}
      className="grid gap-3 rounded-lg border bg-card p-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div>
        <p className="text-sm font-semibold">{COURIER_WORDING.title}</p>
        <p className="text-sm text-muted-foreground">{COURIER_WORDING.hint}</p>
      </div>
      {write.banner && (
        <p role="alert" className="text-sm text-red-600">
          {write.banner}
        </p>
      )}
      {errors.pick_id && (
        <p className="text-sm text-red-600">{errors.pick_id}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {open.length > 1 && (
          <FormField
            id="book-destination"
            label={PICK_WORDING.destination}
            error={errors.delivery_line_id}
          >
            {(control) => (
              <Select
                value={destinationId}
                onValueChange={(next) => {
                  setDestinationId(next);
                  const chosen = open.find((d) => d.delivery_line_id === next);
                  setQuantity(String(chosen ? stillToGo(chosen) : ""));
                  setParcelsEdited(false);
                }}
              >
                <SelectTrigger {...control} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {open.map((d) => (
                    <SelectItem
                      key={d.delivery_line_id}
                      value={d.delivery_line_id}
                    >
                      {pickDestinationLabel(d)} · {count(stillToGo(d))} still to
                      go
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
        <FormField
          id="book-quantity"
          label={PICK_WORDING.quantity}
          error={errors.quantity}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={1}
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
                setParcelsEdited(false);
              }}
            />
          )}
        </FormField>
        <FormField
          id="book-despatched-on"
          label={PICK_WORDING.despatchedOn}
          error={errors.despatched_on}
        >
          {(control) => (
            <Input
              {...control}
              type="date"
              max={pick.today}
              value={despatchedOn}
              onChange={(event) => setDespatchedOn(event.target.value)}
            />
          )}
        </FormField>
        <FormField
          id="book-carrier"
          label={COURIER_WORDING.courier}
          error={errors.carrier}
        >
          {(control) => (
            <Select
              value={carrier}
              onValueChange={(next) => {
                setCarrier(next as BookedCarrier);
                setServiceCode("");
              }}
            >
              <SelectTrigger {...control} className="w-full">
                <SelectValue placeholder={COURIER_WORDING.chooseCourier} />
              </SelectTrigger>
              <SelectContent>
                {carriers.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
        {carrier && (
          <FormField
            id="book-service"
            label={COURIER_WORDING.service}
            error={errors.service_code}
          >
            {(control) => (
              // Radix's hidden native select answers "" when the value lands with its options: never clear on it.
              <Select
                value={serviceCode}
                onValueChange={(next) => next && setServiceCode(next)}
              >
                <SelectTrigger {...control} className="w-full">
                  <SelectValue placeholder={COURIER_WORDING.chooseService} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
        {carrier && (
          <FormField
            id="book-contents"
            label={COURIER_WORDING.contents}
            error={errors.contents}
          >
            {(control) => (
              <Input
                {...control}
                maxLength={25}
                value={contents}
                onChange={(event) => setContents(event.target.value)}
              />
            )}
          </FormField>
        )}
      </div>
      {carrier && (
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">
            {COURIER_WORDING.parcels}{" "}
            <span className="font-normal text-muted-foreground">
              {COURIER_WORDING.parcelsHint}
            </span>
          </legend>
          {(errors.parcels || parcels.length === 0) && (
            <p className="text-sm text-red-600">
              {errors.parcels ?? COURIER_WORDING.addAParcelFirst}
            </p>
          )}
          <ul className="grid gap-1">
            {parcels.map((weight, index) => {
              const parcelId = `book-parcel-${index}`;
              const problem = errors[`parcels.${index}.weight_kg`];
              return (
                <li key={parcelId} className="grid gap-1">
                  <div className="flex items-center gap-2">
                    <span className="w-20 text-sm">
                      {COURIER_WORDING.parcel(index + 1)}
                    </span>
                    <Input
                      id={parcelId}
                      inputMode="decimal"
                      className="h-8 w-28"
                      aria-label={COURIER_WORDING.parcelWeight(index + 1)}
                      value={weight}
                      onChange={(event) => setParcel(index, event.target.value)}
                      aria-invalid={problem ? true : undefined}
                      aria-describedby={
                        problem ? `${parcelId}-error` : undefined
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={COURIER_WORDING.removeParcel(index + 1)}
                      onClick={() => {
                        setParcelsEdited(true);
                        setParcels((current) =>
                          current.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      {COURIER_WORDING.remove}
                    </Button>
                  </div>
                  {problem && (
                    <p
                      id={`${parcelId}-error`}
                      className="text-sm text-red-600"
                    >
                      {problem}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setParcelsEdited(true);
                setParcels((current) => [
                  ...current,
                  current[current.length - 1] ?? "",
                ]);
              }}
            >
              {COURIER_WORDING.addParcel}
            </Button>
          </div>
        </fieldset>
      )}
      {previewNote && (
        <p className="text-sm text-amber-700" role="status">
          {previewNote}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={write.isBusy || !carrier}>
          {COURIER_WORDING.book(carrierName ?? null)}
        </Button>
      </div>
    </form>
  );
}
